import React, { useState, useEffect } from 'react';
import { SpellingGameState } from '../domain/types';
import StarMeter from './StarMeter';
import './SpellingDragDrop.css';

interface SpellingDragDropProps {
  gameState: SpellingGameState;
  onLetterPlaced: (position: number, letter: string) => void;
  onComplete: () => void;
  reducedMotion?: boolean;
  totalStars: number;
}

const SpellingDragDrop: React.FC<SpellingDragDropProps> = ({
  gameState,
  onLetterPlaced,
  onComplete,
  reducedMotion = false,
  totalStars
}) => {
  const [draggedLetter, setDraggedLetter] = useState<string | null>(null);
  const [showWrongFeedback, setShowWrongFeedback] = useState(false);

  const { targetWord, letters, placedLetters } = gameState;
  const targetLetters = targetWord.split('');
  
  // Find the next position to fill
  const nextPosition = placedLetters.findIndex(placed => placed === null);

  const handleDragStart = (e: React.DragEvent, letter: string) => {
    setDraggedLetter(letter);
  };

  const handleDragEnd = () => {
    setDraggedLetter(null);
  };

  const handleDrop = (e: React.DragEvent, position: number) => {
    e.preventDefault();
    if (draggedLetter && !placedLetters[position]) {
      const correctLetter = targetLetters[position];
      if (draggedLetter === correctLetter) {
        onLetterPlaced(position, draggedLetter);
      } else {
        // Show wrong feedback
        setShowWrongFeedback(true);
        setTimeout(() => setShowWrongFeedback(false), 1000);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleLetterClick = (letter: string) => {
    // Find first empty position
    const emptyPosition = placedLetters.findIndex(placed => placed === null);
    if (emptyPosition !== -1) {
      const correctLetter = targetLetters[emptyPosition];
      if (letter === correctLetter) {
        onLetterPlaced(emptyPosition, letter);
      } else {
        // Show wrong feedback
        setShowWrongFeedback(true);
        setTimeout(() => setShowWrongFeedback(false), 1000);
      }
    }
  };

  // Check if word is complete
  useEffect(() => {
    const isComplete = targetLetters.every((letter, index) => 
      placedLetters[index] === letter
    );
    if (isComplete && !gameState.isCompleted) {
      setTimeout(() => onComplete(), 1000);
    }
  }, [placedLetters, targetLetters, gameState.isCompleted, onComplete]);

  return (
    <div className="spelling-drag-drop">
      {/* Header con progreso */}
      <div className="spelling-header">
        <div className="spelling-progress">
          Estrellitas: {totalStars}/5
        </div>
        <StarMeter 
          current={totalStars} 
          total={5}
        />
      </div>

      {showWrongFeedback && (
        <div className="wrong-particles">
          <img 
            src="/assets/Wrong.png" 
            alt="Cata triste" 
            className="wrong-main-image"
          />
          {Array.from({ length: 8 }, (_, i) => (
            <div
              key={i}
              className="wrong-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${1 + Math.random() * 0.5}s`
              }}
            >
              {['😢', '💔', '❌', '😞'][Math.floor(Math.random() * 4)]}
            </div>
          ))}
        </div>
      )}
      
      <div className="target-word">
        <h2>Arrastra las letras para escribir: CATALEYA</h2>
        <div className="word-slots">
          {targetLetters.map((letter, index) => {
            const isFilled = placedLetters[index] !== null;
            const isCurrentTarget = !isFilled && index === nextPosition;
            const isFuture = !isFilled && index > nextPosition;
            
            let slotClass = 'word-slot';
            if (isFilled) {
              slotClass += ' filled';
            } else if (isCurrentTarget) {
              slotClass += ' current-target';
            } else if (isFuture) {
              slotClass += ' future';
            }
            
            return (
              <div
                key={index}
                className={slotClass}
                onDrop={(e) => handleDrop(e, index)}
                onDragOver={handleDragOver}
              >
                {placedLetters[index] || letter}
              </div>
            );
          })}
        </div>
      </div>

      <div className="letter-pool">
        <h3>Letras disponibles:</h3>
        <div className="letters-grid">
          {letters.map((letter, index) => {
            // Count how many times this letter appears in placedLetters
            const placedCount = placedLetters.filter(placed => placed === letter).length;
            // Count how many times this letter appears in the original letters array
            const totalCount = letters.filter(l => l === letter).length;
            // Check if all instances of this letter have been used
            const isUsed = placedCount >= totalCount;
            
            return (
              <div
                key={`${letter}-${index}`}
                className={`letter-tile ${isUsed ? 'used' : 'available'}`}
                draggable={!isUsed}
                onDragStart={(e) => !isUsed && handleDragStart(e, letter)}
                onDragEnd={handleDragEnd}
                onClick={() => !isUsed && handleLetterClick(letter)}
                title={`${letter} (${totalCount - placedCount} disponible${totalCount - placedCount !== 1 ? 's' : ''})`}
              >
                {letter}
              </div>
            );
          })}
        </div>
      </div>

      {gameState.isCompleted && (
        <div className="completion-celebration">
          <img 
            src="/assets/Celebration.png" 
            alt="Cata celebrando" 
            className="celebration-image"
          />
          <div className="celebration-text">
            ¡Excelente, Cata! ✨👑
          </div>
          <div className="celebration-particles">
            {Array.from({ length: 8 }, (_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`
                }}
              >
                {['✨', '🌟', '⭐'][Math.floor(Math.random() * 3)]}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpellingDragDrop;
